import { z } from 'zod';
import { getModel } from '../utils/models';
import { getModelIndexPath, getModelShowPath } from '../utils/routes';
import type { RhinoBrowserTools } from './chat';

const resource = z.string().min(1);
const recordInput = z.object({ resource, id: z.string().regex(/^\d+$/) });
const resultsInput = z.object({
  resource,
  filter_json: z.string().default('{}'),
  search: z.string().optional(),
  order: z.string().optional()
});

export interface RhinoBrowserNavigation {
  kind: 'record' | 'list';
  resource: string;
  label: string;
  id?: string;
  filter?: Record<string, unknown>;
  search?: string;
  order?: string;
}

// Hosts own routing and context. No arbitrary URLs, selectors, or scripts.
export const createChatBrowserTools = ({
  resources,
  navigate,
  readContext
}: {
  resources: readonly string[];
  navigate: (target: RhinoBrowserNavigation) => Promise<void>;
  readContext: () => unknown;
}): RhinoBrowserTools => {
  const checkResource = (name: string) => {
    if (!resources.includes(name))
      throw new Error('Destination is unavailable.');
  };
  return {
    open_record: async (input, { signal }) => {
      const args = recordInput.parse(input);
      checkResource(args.resource);
      signal.throwIfAborted();
      await navigate({ ...args, kind: 'record', label: 'Open record' });
      return { status: 'opened', ...args };
    },
    show_results: async (input, { signal }) => {
      const args = resultsInput.parse(input);
      checkResource(args.resource);
      const filter = z
        .record(z.string(), z.unknown())
        .parse(JSON.parse(args.filter_json));
      signal.throwIfAborted();
      await navigate({
        kind: 'list',
        resource: args.resource,
        filter,
        search: args.search,
        order: args.order,
        label: 'Show results'
      });
      return {
        status: 'opened',
        resource: args.resource,
        filter,
        search: args.search,
        order: args.order
      };
    },
    read_page_context: (_input, { signal }) => {
      signal.throwIfAborted();
      return readContext();
    }
  };
};

export interface ChatNavigationOptions {
  resources: readonly string[];
  // Override a resource's relative path for custom pages. The owner prefix is
  // always supplied by Rhino, never by a tool argument or an override.
  paths?: Record<string, (target: RhinoBrowserNavigation) => string>;
  onShowResults?: () => void;
}

export interface ChatRouteDestination {
  to: string;
  search?: {
    filter: Record<string, unknown>;
    search: string;
    order: string;
    offset: number;
  };
}

export const createChatNavigation =
  ({
    resources,
    ownerId,
    navigate,
    paths = {},
    onShowResults
  }: ChatNavigationOptions & {
    ownerId: number;
    navigate: (destination: ChatRouteDestination) => Promise<void>;
  }) =>
  async (target: RhinoBrowserNavigation): Promise<void> => {
    if (
      !Number.isSafeInteger(ownerId) ||
      ownerId <= 0 ||
      !resources.includes(target.resource)
    ) {
      throw new Error('Destination is unavailable.');
    }
    if (target.kind !== 'record' && target.kind !== 'list')
      throw new Error('Invalid destination.');
    if (target.kind === 'record') recordInput.parse(target);
    const model = getModel(target.resource);
    if (!model) throw new Error('Resource is unavailable.');
    const path =
      paths[target.resource]?.(target) ??
      (target.kind === 'record'
        ? getModelShowPath(model, target.id!)
        : getModelIndexPath(model));
    // Relative paths only; disallow URL/query injection and traversal, including
    // encoded path separators. Search parameters are carried separately.
    if (!/^[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*$/.test(path))
      throw new Error('Invalid destination path.');
    await navigate({
      to: `/${ownerId}/${path}`,
      ...(target.kind === 'list' && {
        search: {
          filter: target.filter ?? {},
          search: target.search ?? '',
          order: target.order ?? '',
          offset: 0
        }
      })
    });
    // Index controllers initialize URL filters on mount. The host can refresh
    // page contents while retaining its persistent chat sheet.
    if (target.kind === 'list') onShowResults?.();
  };
